import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Check, MapPin } from 'lucide-react';
import { ServicePageLayout } from '../components/ServicePageLayout';
import { getCity } from '../data/cityData';
import { BUSINESS_ID } from '../utils/seo';
const relatedServices = [{
  label: 'Interior Detailing',
  to: '/services/interior-detailing'
}, {
  label: 'Exterior Detailing',
  to: '/services/exterior-detailing'
}, {
  label: 'Full Car Detail',
  to: '/services/full-car-detail'
}, {
  label: 'Engine Bay Detailing',
  to: '/services/engine-bay-detailing'
}, {
  label: 'Headlight Restoration',
  to: '/services/headlight-restoration'
}, {
  label: 'Mobile Detailing',
  to: '/services/mobile-detailing'
}, {
  label: 'Ceramic Coating',
  to: '/services/ceramic-coating'
}];
export function CityPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const city = slug ? getCity(slug) : undefined;
  if (!city) {
    return <Navigate to="/" replace data-id="element-549" data-mp="10jx1z0iuw7mt" />;
  }
  const baseUrl = 'https://www.fulldetailpro.com';
  const path = `/ventura-county/${city.slug}`;
  // Described as a Service delivered to this city rather than a LocalBusiness
  // located in it — Mike's Pro Detail is mobile and has no storefront here.
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Mobile Car Detailing in ${city.name}, CA`,
    serviceType: 'Mobile Car Detailing',
    description: city.metaDescription,
    url: `${baseUrl}${path}`,
    provider: {
      '@id': BUSINESS_ID
    },
    areaServed: {
      '@type': 'City',
      name: `${city.name}, CA`
    }
  };
  const breadcrumbs = [{
    name: 'Home',
    item: `${baseUrl}/`
  }, {
    name: 'Service Areas',
    item: `${baseUrl}/#service-areas`
  }, {
    name: city.name,
    item: `${baseUrl}${path}`
  }];
  return <ServicePageLayout title={city.title} subtitle={city.subtitle} metaDescription={city.metaDescription} schema={schema} faqs={city.faqs} breadcrumbs={breadcrumbs} data-id="element-550" data-mp="lj9z6olnae69">
      {city.paragraphs.map((p, i) => <p key={i} data-id="element-551" data-mp="1supwu2al0twt" data-mpi={i}>{p}</p>)}

      <h2 data-id="element-552" data-mp="1stxxqhhs0e1s">Mobile Detailing Services in {city.name}</h2>
      <p data-id="element-553" data-mp="1nw64nk5ealw0">
        We bring the full range of professional auto detailing to {city.name},
        with every service performed on-site at your home or office. Popular
        options for {city.name} drivers include:
      </p>
      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-554" data-mp="1xnsq09bg61ch">
        <li className="flex items-start" data-id="element-555" data-mp="19wwub13velzy">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-556" data-mp="19xopiatk2hpp" />
          <span data-id="element-557" data-mp="14zddbtfsfv4b">
            <strong data-id="element-558" data-mp="14zwmolrdhb6q">Interior Detailing:</strong> Deep vacuuming, shampooing,
            steam sanitizing, and leather conditioning for a like-new cabin.
          </span>
        </li>
        <li className="flex items-start" data-id="element-559" data-mp="1evgcqrj99fnk">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-560" data-mp="1eyvwi7nvjcvb" />
          <span data-id="element-561" data-mp="2uboy0pstjoc3">
            <strong data-id="element-562" data-mp="2ufcthuhtqktg">Exterior Detailing:</strong> Safe hand wash, clay bar
            decontamination, and durable wax or sealant protection.
          </span>
        </li>
        <li className="flex items-start" data-id="element-563" data-mp="2xuebtjwegrh4">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-564" data-mp="2xxk04wx7fpbs" />
          <span data-id="element-565" data-mp="32vvar9vbq3v2">
            <strong data-id="element-566" data-mp="32s7hmbtsjjgv">Full Car Detail:</strong> Our most popular bumper-to-bumper
            package combining interior and exterior services.
          </span>
        </li>
        <li className="flex items-start" data-id="element-567" data-mp="2sww1bp2qi470">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-568" data-mp="3glp0duenks5u" />
          <span data-id="element-569" data-mp="3gmgwd6c5wrwn">
            <strong data-id="element-570" data-mp="3go1f7yp46meo">Engine Bay & Headlight Restoration:</strong> Specialty
            services to keep every part of your vehicle looking its best.
          </span>
        </li>
      </ul>

      <h2 data-id="element-571" data-mp="36vm0da946dy0">Neighborhoods We Serve in {city.name}</h2>
      <p data-id="element-572" data-mp="3bu6zt7gjktle">
        Our mobile detailing service reaches every corner of {city.name},
        including {city.neighborhoods.slice(0, -1).join(', ')}, and{' '}
        {city.neighborhoods[city.neighborhoods.length - 1]}. Wherever you park,
        we come to you with our own water and detailing equipment. We require
        access to a standard electrical outlet at the service location.
      </p>
      <div className="not-prose flex flex-wrap gap-3 my-6" data-id="element-573" data-mp="2bnlo1xm0p00t">
        {city.neighborhoods.map(n => <span key={n} className="inline-flex items-center gap-2 bg-zinc-900/60 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-gray-300" data-id="element-574" data-mp="26o8piflsa4ie">
            <MapPin className="w-4 h-4 text-red-500" data-id="element-575" data-mp="21ribryxgbl05" />
            {n}
          </span>)}
      </div>

      <h2 data-id="element-576" data-mp="2zco26vhg0aqk">Explore Our Detailing Services</h2>
      <p data-id="element-577" data-mp="2uemhk64dc3cz">
        Learn more about each of our professional mobile detailing services
        available in {city.name} and throughout Ventura County:
      </p>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-578" data-mp="2pltd7jctax1f">
        {relatedServices.map(s => <Link key={s.to} to={s.to} className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-579" data-mp="2kmgsp59ey220">
            {s.label} &rarr;
          </Link>)}
      </div>
    </ServicePageLayout>;
}